/**
 * web/src/screens/Notebook/giftflow/hooks.ts — useGiftFlow, the state and
 * actions of the four-step gift flow (card NYJEJRCBfBGQ). All reads and
 * writes go through /api/gift-search, where the giver is the session member
 * on the server; nothing here can name another giver. Ideas are composed
 * locally from the neutral fixture (shared/gift-ideas.ts) — apiserpent
 * credits are 0, so every rec is written with source 'idea' and labelled
 * "Idea, AI-suggested." in the UI, never a live listing.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, session } from '../../../shell/bridge';
import { getMemberId, loadMembers } from '../../../lib/api';
import { partnerNameOf } from '../types';
import { composeGiftIdeaSet } from '../../../../../shared/gift-ideas';
import { giftSearchApi } from './api';
import {
  DEFAULTS_OCCASION,
  draftFromBrief,
  draftFromPrefs,
  minusDays,
  planStepsFor,
  STEP_LABELS,
  type BriefDraft,
  type GiftBrief,
  type GiftFlowView,
  type GiftPlan,
  type GiftSearch,
  type GiftSearchRec,
} from './types';

export interface OccasionRow {
  key: string;
  label: string;
  date: string;
  leadDays: number;
  forMemberId: string | null;
  due: boolean;
}

interface Prefs {
  preference: string | null;
  budgetMax: number | null;
}

interface PartnerInterestsView {
  memberId: string;
  topics: string[];
  completed: boolean;
}

/** How many searches get their rec lists read on a home load, so the Saved
 *  Ideas count is real without fanning out over the whole history. */
const SAVED_SCAN_LIMIT = 10;

export interface GiftFlowState {
  view: GiftFlowView;
  setView: (v: GiftFlowView) => void;
  loading: boolean;
  busy: boolean;
  err: string | null;
  searches: GiftSearch[];
  activeId: string | null;
  active: GiftSearch | null;
  brief: GiftBrief | null;
  recs: GiftSearchRec[];
  plans: GiftPlan[];
  occasions: OccasionRow[];
  prefs: Prefs | null;
  partnerId: string | null;
  partnerName: string;
  partnerTopics: string[];
  /** Set when the brief cleared no quality signal at all: the flow says so and
   *  asks one follow-up instead of showing weak cards (card t23OZwdtxs4k). */
  ideaNoFit: { followUp: string } | null;
  defaultsBrief: GiftBrief | null;
  inProgress: { search: GiftSearch; picked: GiftSearchRec | null; plan: GiftPlan | null } | null;
  savedCount: number;
  savedBySearch: Record<string, number>;
  historyCount: number;
  onOpenUs?: () => void;
  reload: () => Promise<void>;
  start: () => void;
  back: () => void;
  continueWithOccasion: (o: { label: string; date: string | null }) => Promise<void>;
  openSearch: (searchId: string, view: GiftFlowView) => Promise<void>;
  findIdeas: (draft: BriefDraft) => Promise<void>;
  rejectRec: (rec: GiftSearchRec, reason: { label: string; avoid: string }) => Promise<void>;
  saveRec: (rec: GiftSearchRec) => Promise<void>;
  pickRec: (rec: GiftSearchRec) => Promise<void>;
  openDefaults: () => Promise<void>;
  saveDefaults: (draft: BriefDraft) => Promise<void>;
  togglePlanStep: (plan: GiftPlan, index: number) => Promise<void>;
  setPlanStatus: (plan: GiftPlan, status: 'to_acquire' | 'acquired' | 'given') => Promise<void>;
  setReveal: (plan: GiftPlan, mode: 'on_date' | 'private', date: string | null) => Promise<void>;
}

function human(e: unknown, fallback: string): string {
  if (e instanceof Error) {
    try {
      const parsed = JSON.parse(e.message) as { error?: { message?: string }; status?: number };
      if (parsed?.error?.message) return parsed.error.message;
      // A bare status body ({"status":500}) says nothing on its own — keep the
      // fallback and name the status (card Y4ujYPz2zk2K).
      if (typeof parsed?.status === 'number') return `${fallback} (HTTP ${parsed.status})`;
    } catch { /* not a JSON error body */ }
    return e.message || fallback;
  }
  return fallback;
}

export function useGiftFlow({ onOpenUs }: { onOpenUs?: () => void }): GiftFlowState {
  const me = session();
  const members = loadMembers();
  const partnerId = useMemo(
    () => members.find((m) => m.memberId !== (me?.memberId ?? getMemberId()))?.memberId ?? null,
    [members, me],
  );
  const partnerName = partnerNameOf(members, me?.memberId ?? getMemberId());

  const [view, setView] = useState<GiftFlowView>('home');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [searches, setSearches] = useState<GiftSearch[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [brief, setBrief] = useState<GiftBrief | null>(null);
  const [recs, setRecs] = useState<GiftSearchRec[]>([]);
  const [plans, setPlans] = useState<GiftPlan[]>([]);
  const [occasions, setOccasions] = useState<OccasionRow[]>([]);
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [partnerTopics, setPartnerTopics] = useState<string[]>([]);
  const [defaultsBrief, setDefaultsBrief] = useState<GiftBrief | null>(null);
  const [defaultsSearchId, setDefaultsSearchId] = useState<string | null>(null);
  const [savedBySearch, setSavedBySearch] = useState<Record<string, number>>({});
  const [ideaNoFit, setIdeaNoFit] = useState<{ followUp: string } | null>(null);

  /** Bumped by every action that installs a new active search (create, open,
   *  pick, defaults). reload() captures it when it starts and skips
   *  re-pointing activeId when it changed mid-flight — a list fetched before
   *  a create is stale the moment the create lands, and must never clobber
   *  the fresh id (walk-gift-flow: step 3 never mounted — card Y4ujYPz2zk2K). */
  const flowEpochRef = useRef(0);

  /** Load one search's brief + recs + plans into the working state. The row
   *  itself is checked first: a 404 there means the id is stale, which is a
   *  real failure the wall should name (step included), not an empty brief. */
  const loadSearchData = useCallback(async (searchId: string, opts?: { makeActive?: boolean; step?: string }) => {
    const epoch = flowEpochRef.current;
    const step = opts?.step ?? 'this step';
    await giftSearchApi.get(searchId).catch((e) => {
      throw new Error(`${step}: that gift search no longer exists (${human(e, 'request failed')})`);
    });
    const [b, r, p] = await Promise.all([
      giftSearchApi.brief(searchId).then((x) => x.brief).catch(() => null),
      // A brand-new search has no recs row yet; a not_found there must read as
      // "no ideas yet", not abort the whole step (walk-gift-flow caught this as
      // an intermittent "no such gift search row" on Continue — card NYJEJRCBfBGQ).
      giftSearchApi.recs(searchId).then((x) => x.recs).catch(() => [] as GiftSearchRec[]),
      giftSearchApi.plans(searchId).then((x) => x.plans).catch(() => [] as GiftPlan[]),
    ]);
    // A flow action that started while this load was in flight owns the
    // working state now — this (older) load must not overwrite it.
    if (flowEpochRef.current !== epoch) return;
    if (opts?.makeActive !== false) setActiveId(searchId);
    setBrief(b);
    setRecs(r);
    setPlans(p);
  }, []);

  const reload = useCallback(async () => {
    setErr(null);
    const epoch = flowEpochRef.current;
    try {
      const [listRes, occRes, prefRes, intRes] = await Promise.all([
        giftSearchApi.list(),
        api<{ occasions: OccasionRow[] }>('/api/gifts/occasions').catch(() => ({ occasions: [] as OccasionRow[] })),
        api<{ preference: string | null; budgetMax: number | null }>('/api/gifts/preference').catch(() => ({ preference: null, budgetMax: null })),
        api<{ interests: unknown; partner: PartnerInterestsView | null }>('/api/interests').catch(() => ({ interests: null, partner: null })),
      ]);
      const list = listRes.searches;
      // Merge, never replace: a search created while this reload was in flight
      // is absent from `list`, and replacing would drop it from `searches` (so
      // `active` would go null) — card Y4ujYPz2zk2K.
      setSearches((prev) => {
        const fetched = new Set(list.map((s) => s.searchId));
        return [...list, ...prev.filter((s) => !fetched.has(s.searchId))];
      });
      setOccasions(occRes.occasions ?? []);
      setPrefs({ preference: prefRes.preference ?? null, budgetMax: prefRes.budgetMax ?? null });
      setPartnerTopics(intRes.partner?.topics ?? []);
      const visible = list.filter((s) => s.occasionType !== DEFAULTS_OCCASION);
      const defaults = list.find((s) => s.occasionType === DEFAULTS_OCCASION) ?? null;
      setDefaultsSearchId(defaults?.searchId ?? null);
      setDefaultsBrief(defaults ? await giftSearchApi.brief(defaults.searchId).then((x) => x.brief).catch(() => null) : null);
      // Re-point the working search only if no flow action superseded this
      // snapshot while it loaded; a stale list must never overwrite the id a
      // create just installed (walk-gift-flow: step 3 never mounted).
      if (flowEpochRef.current === epoch) {
        const target = visible.find((s) => s.status === 'active' || s.status === 'planned') ?? visible[0] ?? null;
        if (target) await loadSearchData(target.searchId, { step: 'Gifts home' });
        else {
          setActiveId(null);
          setBrief(null);
          setRecs([]);
          setPlans([]);
        }
      }
      // Saved-ideas count across the most recent searches (bounded fan-out).
      const scanned = visible.slice(0, SAVED_SCAN_LIMIT);
      const counts = await Promise.all(scanned.map((s) =>
        giftSearchApi.recs(s.searchId)
          .then((x) => x.recs.filter((r) => r.state === 'saved').length)
          .catch(() => 0),
      ));
      const map: Record<string, number> = {};
      scanned.forEach((s, i) => { map[s.searchId] = counts[i]; });
      setSavedBySearch(map);
    } catch (e) {
      setErr(human(e, 'Could not load your gifts'));
    } finally {
      setLoading(false);
    }
  }, [loadSearchData]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const active = useMemo(() => searches.find((s) => s.searchId === activeId) ?? null, [searches, activeId]);

  const inProgress = useMemo(() => {
    const search = searches.find((s) => s.occasionType !== DEFAULTS_OCCASION && (s.status === 'active' || s.status === 'planned'));
    if (!search) return null;
    const picked = recs.find((r) => r.searchId === search.searchId && r.state === 'picked') ?? null;
    const plan = picked ? plans.find((p) => p.recId === picked.recId) ?? null : null;
    return { search, picked, plan };
  }, [searches, recs, plans]);

  const savedCount = useMemo(() => Object.values(savedBySearch).reduce((a, b) => a + b, 0), [savedBySearch]);
  const historyCount = useMemo(
    () => searches.filter((s) => s.occasionType !== DEFAULTS_OCCASION && s.status === 'given').length,
    [searches],
  );

  const start = useCallback(() => setView(1), []);

  const back = useCallback(() => {
    setView((v) => {
      if (v === 'home' || v === 'defaults') return 'home';
      if (v === 1) return 'home';
      return (v - 1) as GiftFlowView;
    });
  }, []);

  const continueWithOccasion = useCallback(async (o: { label: string; date: string | null }) => {
    if (!partnerId) return;
    flowEpochRef.current += 1;
    setBusy(true);
    setErr(null);
    try {
      const { search } = await giftSearchApi.create({
        recipientId: partnerId,
        occasionType: o.label,
        occasionDate: o.date,
        status: 'active',
      });
      setSearches((prev) => [search, ...prev.filter((s) => s.searchId !== search.searchId)]);
      // The create response is the only source for the id — a value captured
      // before the create is stale by definition (card Y4ujYPz2zk2K).
      await loadSearchData(search.searchId, { step: 'Step 2 · Questions' });
      setView(2);
    } catch (e) {
      setErr(human(e, 'Step 1 · Occasion: could not start that search'));
    } finally {
      setBusy(false);
    }
  }, [partnerId, loadSearchData]);

  const openSearch = useCallback(async (searchId: string, v: GiftFlowView) => {
    flowEpochRef.current += 1;
    setErr(null);
    setIdeaNoFit(null);
    try {
      const step = typeof v === 'number' ? `Step ${v} · ${STEP_LABELS[v - 1]}` : 'Gifts home';
      await loadSearchData(searchId, { step });
      setView(v);
    } catch (e) {
      setErr(human(e, 'Could not open that search'));
    }
  }, [loadSearchData]);

  /** Compose ideas from the brief + the recipient's SHARED topics and write
   *  them as source 'idea' recs. Never a listing: no url, estimate price. */
  const generateIdeas = useCallback(async (search: GiftSearch, b: GiftBrief, avoidOverride?: string[]) => {
    const p = b.parsed;
    const deadline = search.occasionDate ? minusDays(search.occasionDate, 7) : null;
    const avoid = avoidOverride ?? p.avoid;
    const set = composeGiftIdeaSet({
      kind: p.kind,
      vibe: p.vibe,
      budgetMin: p.budgetMin,
      budgetMax: p.budgetMax,
      freeText: b.freeText,
      avoid,
      topics: partnerTopics,
      partnerName,
      deadline,
      seed: b.version * 977 + recs.length,
    });
    // Nothing cleared the floor: write no recs at all and let step 3 ask the
    // one follow-up question. Padding three weak guesses is the defect the
    // floor exists to prevent (card t23OZwdtxs4k).
    setIdeaNoFit(set.noFit ? { followUp: set.followUp ?? '' } : null);
    for (const idea of set.ideas) {
      await giftSearchApi.addRec(search.searchId, {
        source: idea.source,
        title: idea.title,
        priceEstimate: idea.priceEstimate,
        rationale: idea.rationale,
        deadline: idea.deadline,
      });
    }
  }, [partnerTopics, partnerName, recs.length]);

  const findIdeas = useCallback(async (draft: BriefDraft) => {
    if (!activeId || !active) {
      setErr('Step 2 · Questions: no gift search is active — go back to step 1 and choose an occasion.');
      return;
    }
    if (active.occasionType === DEFAULTS_OCCASION) {
      setErr('Step 2 · Questions: gift defaults never run the four-step flow — choose an occasion in step 1.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const { brief: saved } = await giftSearchApi.saveBrief(activeId, {
        freeText: draft.freeText,
        parsed: {
          kind: draft.kind,
          budgetMin: draft.budgetMin,
          budgetMax: draft.budgetMax,
          currency: 'AUD',
          vibe: draft.vibe,
          avoid: brief?.parsed.avoid ?? [],
          timing: active.occasionDate ?? null,
          surpriseLevel: null,
        },
      });
      await generateIdeas(active, saved);
      const [b, r] = await Promise.all([
        giftSearchApi.brief(activeId).then((x) => x.brief),
        giftSearchApi.recs(activeId).then((x) => x.recs),
      ]);
      setBrief(b);
      setRecs(r);
      setView(3);
    } catch (e) {
      setErr(human(e, 'Step 2 · Questions: could not find ideas'));
    } finally {
      setBusy(false);
    }
  }, [activeId, active, brief, generateIdeas]);

  const rejectRec = useCallback(async (rec: GiftSearchRec, reason: { label: string; avoid: string }) => {
    if (!activeId) return;
    setBusy(true);
    setErr(null);
    try {
      await giftSearchApi.updateRec(rec.recId, { state: 'rejected', rejectReason: reason.label });
      const avoid = [...(brief?.parsed.avoid ?? []), reason.avoid];
      const { brief: saved } = await giftSearchApi.saveBrief(activeId, {
        freeText: brief?.freeText ?? '',
        parsed: { ...(brief?.parsed ?? {}), avoid, currency: 'AUD' },
      });
      // Refresh: regenerate so the avoid item shapes the next three.
      await generateIdeas(active!, saved, avoid);
      const [b, r] = await Promise.all([
        giftSearchApi.brief(activeId).then((x) => x.brief),
        giftSearchApi.recs(activeId).then((x) => x.recs),
      ]);
      setBrief(b);
      setRecs(r);
    } catch (e) {
      setErr(human(e, 'Step 3 · Ideas: could not save that'));
    } finally {
      setBusy(false);
    }
  }, [activeId, active, brief, generateIdeas]);

  const saveRec = useCallback(async (rec: GiftSearchRec) => {
    setErr(null);
    try {
      await giftSearchApi.updateRec(rec.recId, { state: 'saved' });
      if (activeId) setRecs((await giftSearchApi.recs(activeId)).recs);
    } catch (e) {
      setErr(human(e, 'Could not save that idea'));
    }
  }, [activeId]);

  const pickRec = useCallback(async (rec: GiftSearchRec) => {
    if (!activeId || !active) return;
    flowEpochRef.current += 1;
    setBusy(true);
    setErr(null);
    try {
      await giftSearchApi.updateRec(rec.recId, { state: 'picked' });
      const steps = planStepsFor(brief?.parsed.kind ?? null, rec.deadline);
      await giftSearchApi.createPlan(activeId, {
        recId: rec.recId,
        steps,
        status: 'to_acquire',
        revealMode: 'private',
      });
      await giftSearchApi.update(activeId, { status: 'planned' });
      setSearches((prev) => prev.map((s) => (s.searchId === activeId ? { ...s, status: 'planned' } : s)));
      await loadSearchData(activeId, { step: 'Step 4 · Plan' });
      setView(4);
    } catch (e) {
      setErr(human(e, 'Step 3 · Ideas: could not pick that'));
    } finally {
      setBusy(false);
    }
  }, [activeId, active, brief, loadSearchData]);

  const openDefaults = useCallback(async () => {
    flowEpochRef.current += 1;
    setBusy(true);
    setErr(null);
    try {
      let id = defaultsSearchId;
      if (!id) {
        if (!partnerId) throw new Error('no partner yet');
        const { search } = await giftSearchApi.create({
          recipientId: partnerId,
          occasionType: DEFAULTS_OCCASION,
          status: 'draft',
        });
        id = search.searchId;
        setSearches((prev) => [search, ...prev]);
        setDefaultsSearchId(id);
      }
      // The defaults search feeds the defaults editor only — it must never
      // become the active search of the four-step flow (card Y4ujYPz2zk2K).
      await loadSearchData(id, { makeActive: false, step: 'Gift defaults' });
      setView('defaults');
    } catch (e) {
      setErr(human(e, 'Could not open your defaults'));
    } finally {
      setBusy(false);
    }
  }, [defaultsSearchId, partnerId, loadSearchData]);

  const saveDefaults = useCallback(async (draft: BriefDraft) => {
    if (!defaultsSearchId) return;
    setBusy(true);
    setErr(null);
    try {
      const { brief: saved } = await giftSearchApi.saveBrief(defaultsSearchId, {
        freeText: draft.freeText,
        parsed: {
          kind: draft.kind,
          budgetMin: draft.budgetMin,
          budgetMax: draft.budgetMax,
          currency: 'AUD',
          vibe: draft.vibe,
          avoid: defaultsBrief?.parsed.avoid ?? [],
          timing: null,
          surpriseLevel: null,
        },
      });
      setDefaultsBrief(saved);
      setView('home');
      await reload();
    } catch (e) {
      setErr(human(e, 'Could not save your defaults'));
    } finally {
      setBusy(false);
    }
  }, [defaultsSearchId, defaultsBrief, reload]);

  const togglePlanStep = useCallback(async (plan: GiftPlan, index: number) => {
    setErr(null);
    try {
      const steps = plan.steps.map((s, i) => (i === index ? { ...s, done: !s.done } : s));
      const { plan: updated } = await giftSearchApi.updatePlan(plan.planId, { steps });
      setPlans((prev) => prev.map((p) => (p.planId === updated.planId ? updated : p)));
    } catch (e) {
      setErr(human(e, 'Could not update the plan'));
    }
  }, []);

  const setPlanStatus = useCallback(async (plan: GiftPlan, status: 'to_acquire' | 'acquired' | 'given') => {
    setErr(null);
    // The tapped status is written optimistically so the control answers the
    // tap in the same frame; the PATCH's returned plan then replaces it with
    // the server's answer, and a rejection rolls the chip back to the status
    // the server still holds. Before, the response was the only write, so a
    // slow round trip left the control showing the old value after the save
    // had already landed server-side (card 6KO4bwTm7eHB).
    setPlans((prev) => prev.map((p) => (p.planId === plan.planId ? { ...p, status } : p)));
    try {
      const { plan: updated } = await giftSearchApi.updatePlan(plan.planId, { status });
      setPlans((prev) => prev.map((p) => (p.planId === updated.planId ? updated : p)));
      setSearches((prev) => prev.map((s) => (s.searchId === plan.searchId ? { ...s, status: status === 'given' ? 'given' : s.status } : s)));
    } catch (e) {
      // Roll back only while the control still shows the tapped status — a
      // newer tap (or a fresh load) owns the state by then.
      setPlans((prev) => prev.map((p) => (p.planId === plan.planId && p.status === status ? { ...p, status: plan.status } : p)));
      setErr(human(e, 'Could not update the status'));
    }
  }, []);

  const setReveal = useCallback(async (plan: GiftPlan, mode: 'on_date' | 'private', date: string | null) => {
    setErr(null);
    try {
      const { plan: updated } = await giftSearchApi.updatePlan(plan.planId, { revealMode: mode, revealDate: date });
      setPlans((prev) => prev.map((p) => (p.planId === updated.planId ? updated : p)));
    } catch (e) {
      setErr(human(e, 'Could not save the reveal choice'));
    }
  }, []);

  return {
    view, setView,
    loading, busy, err,
    searches, activeId, active, brief, recs, plans,
    occasions, prefs, partnerId, partnerName, partnerTopics,
    ideaNoFit,
    defaultsBrief, inProgress, savedCount, savedBySearch, historyCount,
    onOpenUs,
    reload, start, back, continueWithOccasion, openSearch,
    findIdeas, rejectRec, saveRec, pickRec,
    openDefaults, saveDefaults,
    togglePlanStep, setPlanStatus, setReveal,
  };
}

/** The Questions-step prefill: the search's own brief when it has one (an
 *  edit keeps what was there), else the Gift defaults, else the legacy
 *  preference/budget. */
export function prefillDraft(g: Pick<GiftFlowState, 'brief' | 'defaultsBrief' | 'prefs'>): BriefDraft {
  if (g.brief) return draftFromBrief(g.brief);
  if (g.defaultsBrief) return draftFromBrief(g.defaultsBrief);
  return draftFromPrefs(g.prefs?.preference ?? null, g.prefs?.budgetMax ?? null);
}
